import { Component, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import jQuery from "jquery";
import { EventBusStatus } from "./components/EventBusStatus.jsx";
import { ConsultFormMount } from "./consult-form.jsx";
import { SearchPanel, SiteHeader } from "./homepage/components/SiteHeader.jsx";
import {
  installMinioAssetRewriter,
  rewriteAssetMarkup,
} from "./modules/minio-assets.js";
import { activateScripts, templateHtml } from "./modules/react-html-shell.js";
import { FormDraftPersistence, formDraftStore } from "./state/form-drafts.jsx";

// Legacy Blade handlers still use `$`; keep it inside the Vite dependency graph.
window.$ = window.jQuery = jQuery;

jQuery(document).on(
  "ajaxSuccess.squraFrontendDrafts",
  (_event, _xhr, settings, response) => {
    const method = String(settings?.type || "GET").toUpperCase();
    if (
      ["POST", "PUT", "PATCH"].includes(method) &&
      response?.status !== "false"
    ) {
      document.dispatchEvent(
        new CustomEvent("squra:form-saved", {
          detail: { root: "#react-frontend-root" },
        }),
      );
    }
  },
);

class FrontendErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="sq-react-error" role="alert">
        <strong>Halaman tidak dapat ditampilkan.</strong>
        <button type="button" onClick={() => window.location.reload()}>
          Muat ulang
        </button>
      </div>
    );
  }
}

function SharedSiteHeader() {
  const [config, setConfig] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const lang =
      document.documentElement.lang?.toUpperCase() === "ID" ? "ID" : "EN";

    fetch(`/api/v1/homepage?lang=${lang}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload) throw new Error("Menu gagal dimuat.");
        setConfig(payload);
      })
      .catch((error) => {
        if (error.name !== "AbortError")
          console.error("Shared header error", error);
      });

    return () => controller.abort();
  }, []);

  if (!config)
    return <div className="sq-shared-header-loading" aria-hidden="true" />;

  return (
    <div className="sq-react-homepage sq-shared-site-header">
      <SiteHeader config={config} onSearch={() => setSearchOpen(true)} />
      <SearchPanel
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        config={config}
      />
    </div>
  );
}

function FrontendApp() {
  const [navigating, setNavigating] = useState(false);
  const shell = useRef(null);
  const [html] = useState(() =>
    rewriteAssetMarkup(templateHtml("react-frontend-template")),
  );

  useLayoutEffect(() => {
    if (shell.current) activateScripts(shell.current);
    window.__resolveSquraFrontendShell?.();
  }, []);

  useEffect(() => {
    const uninstallAssets = installMinioAssetRewriter();
    const onClick = (event) => {
      const link = event.target.closest("a[href]");
      if (
        !link ||
        event.defaultPrevented ||
        event.button !== 0 ||
        link.target === "_blank" ||
        link.hasAttribute("download")
      )
        return;
      const target = new URL(link.href, window.location.href);
      if (
        target.origin === window.location.origin &&
        target.href !== window.location.href &&
        !target.hash
      )
        setNavigating(true);
    };
    const stop = () => setNavigating(false);
    document.addEventListener("click", onClick);
    window.addEventListener("pageshow", stop);
    return () => {
      uninstallAssets();
      document.removeEventListener("click", onClick);
      window.removeEventListener("pageshow", stop);
    };
  }, []);

  return (
    <>
      <SharedSiteHeader />
      <div
        className="react-html-shell"
        ref={shell}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {createPortal(
        <>
          <div
            className={`sq-react-route-progress${navigating ? " is-active" : ""}`}
            aria-hidden="true"
          />
          <span
            className="sq-react-runtime"
            title="Frontend dikelola React JSX"
          >
            React JSX
          </span>
          <EventBusStatus variant="frontend" />
          <FormDraftPersistence scope="frontend" />
          <ConsultFormMount />
        </>,
        document.body,
      )}
    </>
  );
}

const root = document.getElementById("react-frontend-root");
if (root)
  createRoot(root).render(
    <Provider store={formDraftStore}>
      <FrontendErrorBoundary>
        <FrontendApp />
      </FrontendErrorBoundary>
    </Provider>,
  );
